import type { RootState } from "../store/rootReducer";
import { selectAllRecipes } from "../store/slices/recipesSlice";
import { selectAllIngredients } from "../store/slices/ingredientsSlice";
import { selectAllMethodSteps } from "../store/slices/methodStepsSlice";
import { selectAllCollections } from "../store/slices/collectionsSlice";
import { selectAllRecipeCollections } from "../store/slices/recipeCollections";

// Fields that are sync bookkeeping or device-local, not user content — dropped
// from the export so the file is a clean, portable copy of the user's recipes.
const OMIT = new Set([
  "id",
  "recipeId",
  "userId",
  "createdAt",
  "updatedAt",
  "deletedAt",
  "sortOrder",
  "stepNumber",
  "localImageUri",
  "imagePendingUpload",
  "cachedImageUri",
]);

function clean(obj: object): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (!OMIT.has(k) && v !== undefined && v !== null && v !== "") out[k] = v;
  }
  return out;
}

// Assemble a human-readable, portable snapshot of the user's data (recipes with
// their ingredients/method nested, plus collection names). Takes the store state
// so it's platform-agnostic; the selectAll* selectors already exclude soft-deleted
// rows.
export function buildExportData(state: RootState, email?: string) {
  const recipes = selectAllRecipes(state);
  const ingredients = selectAllIngredients(state);
  const steps = selectAllMethodSteps(state);
  const collections = selectAllCollections(state);
  const recipeCollections = selectAllRecipeCollections(state);

  const collName = new Map(collections.map((c) => [c.id, c.name]));

  const exportRecipes = recipes.map((recipe) => ({
    ...clean(recipe),
    ingredients: ingredients
      .filter((i) => i.recipeId === recipe.id)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(clean),
    method: steps
      .filter((s) => s.recipeId === recipe.id)
      .sort((a, b) => a.stepNumber - b.stepNumber)
      .map((s) => s.instruction),
    collections: recipeCollections
      .filter((rc) => rc.recipeId === recipe.id)
      .map((rc) => collName.get(rc.collectionId))
      .filter(Boolean),
  }));

  return {
    app: "RecipeRoll",
    exportedAt: new Date().toISOString(),
    ...(email ? { account: { email } } : {}),
    recipeCount: exportRecipes.length,
    collections: collections.map((c) => c.name),
    recipes: exportRecipes,
  };
}
